import React, { useState } from 'react';

import { notify } from '@NOTIFICATION/Notificator';

import { EntityTable, EntityFormModal } from '@COMPONENTS/SHARED/EntityManagement';

import {
    useGetAllCountriesAdmin,
    useCreateCountry,
    useUpdateCountry,
    useDeleteCountry,
    useGetAllRegionsAdmin,
    useGetAllAreasAdmin,
} from '@SERVICES';
import { Country } from '@INTERFACES/api/countries';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function CountriesTab() {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingCountry, setEditingCountry] = useState<Country | null>(null);

    const { data: countries = [], isLoading, refetch } = useGetAllCountriesAdmin();
    const { data: regions = [] } = useGetAllRegionsAdmin();
    const { data: areas = [] } = useGetAllAreasAdmin();

    const [createCountry, { isLoading: isCreating }] = useCreateCountry();
    const [updateCountry, { isLoading: isUpdating }] = useUpdateCountry();
    const [deleteCountry] = useDeleteCountry();

    const columns = [
        {
            key: 'id' as keyof Country,
            header: 'ID',
        },
        {
            key: 'name' as keyof Country,
            header: 'Name',
        },
        {
            key: 'iso_code' as keyof Country,
            header: 'ISO Code',
            render: (isoCode: string) => (
                <span style={{ 
                    fontFamily: 'monospace',
                    backgroundColor: '#f8f9fa',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '12px',
                    fontWeight: '500',
                }}
                >
                    {isoCode}
                </span>
            ),
        },
        {
            key: 'region' as keyof Country,
            header: 'Region',
            render: (regionId: number) => {
                const region = regions.find((r) => r.id === regionId);
                return region ? region.name : `ID: ${regionId}`;
            },
        },
        {
            key: 'areas' as keyof Country,
            header: 'Areas',
            render: (areaIds: number[]) => {
                if (!areaIds || areaIds.length === 0) return '-';
                const areaNames = areaIds.map((areaId) => {
                    const area = areas.find((a) => a.id === areaId);
                    return area ? area.name : `ID: ${areaId}`;
                });
                return areaNames.join(', ');
            },
        },
    ];

    const formFields = [
        {
            key: 'name',
            label: 'Name',
            type: 'text' as const,
            required: true,
            placeholder: 'Enter country name (e.g., "United States", "Germany")',
            helpText: 'Use standard country names for best map compatibility. Check the mapping guide below.',
        },
        {
            key: 'iso_code',
            label: 'ISO Code',
            type: 'text' as const,
            required: true,
            placeholder: 'Enter 3-letter ISO code (e.g., "CHE" for Switzerland, "DEU" for Germany)',
            helpText: 'Required: 3-letter ISO country code (ISO 3166-1 alpha-3 format). Examples: USA, GBR, FRA, DEU, CHE, JPN, AUS, CAN, BRA, IND, CHN, RUS',
            validation: {
                pattern: {
                    value: /^[A-Z]{3}$/,
                    message: 'ISO code must be exactly 3 uppercase letters (e.g., "USA", "DEU", "CHE")',
                },
                minLength: {
                    value: 3,
                    message: 'ISO code must be exactly 3 characters',
                },
                maxLength: {
                    value: 3,
                    message: 'ISO code must be exactly 3 characters',
                },
            },
        },
        {
            key: 'region',
            label: 'Region',
            type: 'select' as const,
            required: true,
            options: regions.map((region) => ({
                value: region.id,
                label: region.name,
            })),
        },
        {
            key: 'areas',
            label: 'Areas',
            type: 'multiselect' as const,
            placeholder: 'Select areas',
            options: areas.map((area) => ({
                value: area.id,
                label: area.name,
            })),
        },
    ];

    const handleCreate = () => {
        setEditingCountry(null);
        setIsFormModalOpen(true);
    };

    const handleEdit = (country: Country) => {
        console.log('Original country data:', country);
        
        // For HTML select, areas should remain as array of IDs
        setEditingCountry(country);
        setIsFormModalOpen(true);
    };

    const handleDelete = async (country: Country) => {
        if (window.confirm(`Are you sure you want to delete "${country.name}"?`)) {
            try {
                await deleteCountry(country.id).unwrap();
                notify.success('Country deleted successfully!');
                refetch();
            } catch (error) {
                // Extract specific error message from API response
                let errorMessage = 'Failed to delete country!';
                
                if (error && typeof error === 'object' && 'status' in error) {
                    const fetchError = error as FetchBaseQueryError;
                    if (typeof fetchError.status === 'number' && fetchError.data) {
                        // Try to extract the message from the API response
                        const errorData = fetchError.data as any;
                        if (errorData && typeof errorData === 'object' && errorData.message) {
                            errorMessage = errorData.message;
                        }
                    }
                }
                
                notify.error(errorMessage);
            }
        }
    };

    const handleFormSubmit = async (formData: any) => {
        try {
            console.log('Form data received:', formData);
            console.log('Is editing:', !!editingCountry);

            if (editingCountry) {
                // For updates, only send the fields that should be updated
                const updateData = {
                    id: editingCountry.id,
                    name: formData.name,
                    iso_code: formData.iso_code?.toUpperCase() || '',
                    region_id: parseInt(formData.region, 10),
                    areas: formData.areas || [],
                };
                
                console.log('Updating country with data:', updateData);
                await updateCountry(updateData).unwrap();
                notify.success('Country updated successfully!');
            } else {
                // For creates, process the form data as before
                const createData = {
                    name: formData.name,
                    iso_code: formData.iso_code?.toUpperCase() || '',
                    region_id: parseInt(formData.region, 10),
                    areas: formData.areas || [],
                };
                
                console.log('Creating country with data:', createData);
                await createCountry(createData).unwrap();
                notify.success('Country created successfully!');
            }
            setIsFormModalOpen(false);
            setEditingCountry(null);
            refetch();
        } catch (error) {
            console.error('Form submission error:', error);
            
            // More detailed error handling
            let errorMessage = `Failed to ${editingCountry ? 'update' : 'create'} country!`;
            
            if (error && typeof error === 'object') {
                if ('data' in error) {
                    const errorData = (error as any).data;
                    if (typeof errorData === 'object' && errorData) {
                        // Log the full error for debugging
                        console.error('API Error Details:', errorData);
                        
                        // Try to extract field-specific errors
                        if (errorData.message) {
                            errorMessage = errorData.message;
                        } else if (typeof errorData === 'object') {
                            // Handle validation errors
                            const fieldErrors = Object.entries(errorData)
                                .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                                .join('; ');
                            if (fieldErrors) {
                                errorMessage = `Validation errors: ${fieldErrors}`;
                            }
                        }
                    }
                }
                
                if ('status' in error) {
                    console.error('HTTP Status:', (error as any).status);
                }
            }
            
            notify.error(errorMessage);
        }
    };

    const handleFormClose = () => {
        setIsFormModalOpen(false);
        setEditingCountry(null);
    };

    return (
        <div style={{ padding: '24px' }}>
            <EntityTable
                data={countries}
                columns={columns}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreate={handleCreate}
                isLoading={isLoading}
                entityName="Country"
            />

            <EntityFormModal
                isOpen={isFormModalOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                title={editingCountry ? 'Edit Country' : 'Create Country'}
                fields={formFields}
                initialData={editingCountry}
                isLoading={isCreating || isUpdating}
            />
        </div>
    );
}

export default CountriesTab;
